import { isProxy, ref, toRaw } from 'vue'
import {
  ConcurrentChangeError,
  type OrganizationKey,
} from '~~/errors/ConcurrentChangeError'
import {
  applyConflictChoices,
  buildConcurrentPatch,
  type ConflictChoice,
  type MergeRecord,
} from '~~/utils/organization/concurrentPatch'
import { useSourceToast } from '~/composables/ui/useSourceToast'

const KEY_FIELDS = ['dirLib', 'domLib', 'ageLib', 'equLib'] as const

export function useConcurrentForm() {
  const toast = useSourceToast()
  const savedBase = ref<MergeRecord | null>(null)
  const conflict = ref<ConcurrentChangeError | null>(null)
  const deletedOpen = ref(false)
  const choices = ref<Record<string, ConflictChoice>>({})

  function rememberBase(value: object): void {
    savedBase.value = plainFormSnapshot(value)
  }

  function captureError(error: unknown): boolean {
    if (!(error instanceof ConcurrentChangeError)) {
      return false
    }
    if (error.code === 'deleted') {
      deletedOpen.value = true
      return true
    }
    const next: Record<string, ConflictChoice> = {}
    for (const field of error.fields) {
      next[field.field] = 'mine'
    }
    choices.value = next
    conflict.value = error
    return true
  }

  function closeConflict(): void {
    conflict.value = null
    deletedOpen.value = false
  }

  function acceptChoices(form: object): void {
    if (conflict.value === null || savedBase.value === null) {
      return
    }
    applyConflictChoices(form as MergeRecord, savedBase.value, conflict.value.fields, choices.value)
    closeConflict()
  }

  function buildPatch(current: object, fields: readonly string[]) {
    if (savedBase.value === null) {
      return null
    }
    return buildConcurrentPatch(current as MergeRecord, savedBase.value, fields)
  }

  async function noteParentRename(
    previousKey: string,
    currentKey: OrganizationKey | null | undefined,
    changes: MergeRecord
  ): Promise<boolean> {
    if (currentKey == null || !parentSegmentChanged(previousKey, currentKey, changes)) {
      return false
    }
    toast.info('Renommé entre-temps', 'Votre modification a bien été enregistrée.')
    await navigateTo(organizationPath(currentKey, previousKey.split('~').length), { replace: true })
    return true
  }

  return {
    savedBase,
    conflict,
    deletedOpen,
    choices,
    rememberBase,
    captureError,
    closeConflict,
    acceptChoices,
    buildPatch,
    noteParentRename,
  }
}

function plainFormSnapshot(value: object): MergeRecord {
  return copyPlain(value) as MergeRecord
}

function copyPlain(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) {
    return value
  }
  const raw = isProxy(value) ? toRaw(value) : value
  if (Array.isArray(raw)) {
    return raw.map((item) => copyPlain(item))
  }
  const output: Record<string, unknown> = {}
  for (const key of Object.keys(raw)) {
    output[key] = copyPlain((raw as Record<string, unknown>)[key])
  }
  return output
}

function parentSegmentChanged(
  previousKey: string,
  currentKey: OrganizationKey,
  changes: MergeRecord
): boolean {
  const previous = previousKey.split('~')
  for (let index = 0; index < previous.length; index += 1) {
    const field = KEY_FIELDS[index]
    const nextValue = field === undefined ? undefined : currentKey[field]
    if (typeof nextValue !== 'string' || nextValue === '' || nextValue === previous[index]) {
      continue
    }
    if (field !== undefined && changes[field] === undefined) {
      return true
    }
  }
  return false
}

function organizationPath(currentKey: OrganizationKey, depth: number): string {
  const parts: string[] = []
  const limit = Math.min(depth, KEY_FIELDS.length)
  for (let index = 0; index < limit; index += 1) {
    const field = KEY_FIELDS[index]
    const value = field === undefined ? '' : currentKey[field]
    if (typeof value === 'string' && value !== '') {
      parts.push(encodeURIComponent(value))
    }
  }
  return `/${parts.join('/')}`
}
